/**
 * Smaller renditions of supplier photos, for hosts that serve them at a predictable address.
 *
 * Travelport's room search asks for its largest image size, and from Leonardo (the media host behind
 * Marriott, Ritz-Carlton, St Regis and some Hilton photos) that is the photographer's original: 6016×4016,
 * ~8 MB, ~96 MB once decoded. A room card draws it a few hundred pixels wide, and the Rooms grid holds
 * dozens of them, which is what locked the guide up on scroll. Leonardo serves the same photo at fixed
 * widths, named by the letter before the extension (`…_O.jpg` is the original); the widths below were
 * measured on 2026-10-16. Any other host's URL is returned unchanged.
 *
 * Both platforms' `AppImage` go through `renditionCandidates`, so no screen fetches a 6016px original:
 * a call site that knows how wide it draws a photo passes `displayWidth` for the closest fit, and every
 * other one gets the widest rendition.
 */

/** Leonardo's renditions, narrowest first: [letter, width in pixels]. `_O`, the original, is the fallback. */
const LEONARDO_RENDITIONS = [
  ['F', 300],
  ['M', 400],
  ['J', 480],
  ['Z', 599],
  ['P', 800],
  ['T', 1786],
  ['S', 2048],
] as const;

/** A Leonardo photo URL, split around its rendition letter. */
const LEONARDO_PHOTO_URL = /^(https:\/\/[^/]*leonardocontentcloud\.com\/.+_)([A-Z])(\.(?:jpe?g|png|webp))$/i;

/**
 * The smallest rendition of `url` at least `pixelWidth` wide (device pixels: a CSS width times the
 * device pixel ratio), or the widest rendition when none is that wide. The original never comes back
 * from here: past 2048px no card, gallery or lightbox gains anything from 6016. Unknown hosts are
 * returned as they are.
 *
 * Pass the original as a fallback alongside the result (`AppImage` takes a list of candidates): the
 * renditions are Leonardo's convention, not a contract, and a photo without one should still show.
 *
 * @example
 * imageUrlForWidth('https://travelport.leonardocontentcloud.com/imageRepo/7/0/142/919/775/NYCHA_7313341557_O.jpg', 640)
 * // → '…/NYCHA_7313341557_P.jpg' (800px wide)
 */
export function imageUrlForWidth(url: string, pixelWidth: number): string {
  const match = LEONARDO_PHOTO_URL.exec(url);
  if (!match) return url;
  const [, prefix, , extension] = match;
  const [letter] = LEONARDO_RENDITIONS.find(([, width]) => width >= pixelWidth) ?? LEONARDO_RENDITIONS.at(-1)!;
  return `${prefix}${letter}${extension}`;
}

/** What `renditionCandidates` fetches for a call site that gives no width: the widest rendition. */
export const DEFAULT_RENDITION_WIDTH = 2048;

/**
 * An image's candidate URLs (best first) with each one's rendition put before it, de-duplicated: the
 * rendition is fetched, and the original only if the rendition fails. `pixelWidth` is in device pixels;
 * absent, the widest rendition. A URL from a host without renditions appears once, unchanged.
 *
 * @example
 * renditionCandidates(['…/A_O.jpg', 'https://other.example/b.jpg'], 640)
 * // → ['…/A_P.jpg', '…/A_O.jpg', 'https://other.example/b.jpg']
 */
export function renditionCandidates(
  src: string | ReadonlyArray<string | undefined>,
  pixelWidth: number = DEFAULT_RENDITION_WIDTH,
): string[] {
  const urls = (typeof src === 'string' ? [src] : src).filter((url): url is string => !!url);
  return [...new Set(urls.flatMap((url) => [imageUrlForWidth(url, pixelWidth), url]))];
}
